import React from "react";
import { FaFile } from "react-icons/fa";
import { FiEdit } from "react-icons/fi";
import { MdTimeline } from "react-icons/md";
import { FormattedMessage, useIntl } from "react-intl";

import { queries } from "lib/build/dashboards/basic-health/queries";
import { Status } from "lib/build/forms/basic-health/patientCondition";

import { Tooltip } from "src/common/components/Tooltip";
import { useFormatName } from "src/common/hooks/useFormatName";
import {
	Permission,
	useAuthState,
	useHasPermission,
	useKnownUsers
} from "src/features/auth";
import {
	useLastEventByQuery,
	useLastEventValueByQuery
} from "src/features/events";
import {
	PatientLink,
	usePatientIdParam,
	usePatientName,
	useSelectPatient
} from "src/features/patient";
import { node as patientStatementOfConsent } from "src/modules/basic-health/forms/patientStatementOfConsent";
import { ShortStatus } from "src/modules/basic-health/header/components/ShortStatus";
import { useAge } from "src/modules/basic-health/header/hooks/useAge";
import { useBloodGroup } from "src/modules/basic-health/header/hooks/useBloodGroup";
import { useBmi } from "src/modules/basic-health/header/hooks/useBmi";
import { useHeight } from "src/modules/basic-health/header/hooks/useHeight";
import { useLongStatus } from "src/modules/basic-health/header/hooks/useLongStatus";
import { useSocialSecurityNumber } from "src/modules/basic-health/header/hooks/useSocialSecurityNumber";
import { useWeight } from "src/modules/basic-health/header/hooks/useWeight";
import { messages } from "src/modules/basic-health/header/messages";
import {
	Header as BareHeader,
	HeaderAlert,
	HeaderStatus,
	HeaderValue,
	renderTitle
} from "src/modules/shared/header";

import "./Header.css";

export const Header: React.FunctionComponent = () => {
	const canEdit = useCanEdit();
	const canViewTimeline = useCanViewTimeline();
	const status =
		useLastEventValueByQuery<Status>(queries.patientStatus) || Status.NEW;
	const longStatus = useLongStatus();
	return (
		<BareHeader
			variant={status}
			status={
				<PatientLink to="/status-history">
					<HeaderStatus progress={0} title={longStatus}>
						<ShortStatus />
					</HeaderStatus>
				</PatientLink>
			}
			title={
				<PatientLink to="/dashboard">
					<Name />
				</PatientLink>
			}
			actions={
				<>
					<StatementOfConsentAlert />
					{canEdit && <EditLink />}
					{canViewTimeline && <TimelineLink />}
					<ReportLink />
				</>
			}
			values={
				<>
					<HeaderValue variant="social-security-number">
						<SocialSecurityNumber />
					</HeaderValue>
					<HeaderValue variant="blood-group">
						<BloodGroup />
					</HeaderValue>
					<HeaderValue variant="age">
						<Age />
					</HeaderValue>
					<HeaderValue variant="weight">
						<Weight />
					</HeaderValue>
					<HeaderValue variant="height">
						<Height />
					</HeaderValue>
					<HeaderValue variant="bmi">
						<FormattedMessage {...messages.bmi} />
						{": "}
						<Bmi />
					</HeaderValue>
				</>
			}
		/>
	);
};

function useCanEdit(): boolean {
	const patientId = usePatientIdParam();

	const { user } = useAuthState();
	const { patient } = useSelectPatient(patientId);

	const hasPatientsAllRWPerm = useHasPermission(Permission.PatientsAllRW);

	const isConnectedUser =
		user !== null &&
		patient !== null &&
		patient.connectedUsers &&
		patient.connectedUsers.includes(user.id);

	return hasPatientsAllRWPerm || isConnectedUser;
}

function useCanViewTimeline(): boolean {
	return useHasPermission(Permission.Timeline);
}

const EditLink: React.FunctionComponent = () => {
	const { formatMessage } = useIntl();
	return (
		<PatientLink to="edit" title={formatMessage(messages.editPatient)}>
			<FiEdit />
		</PatientLink>
	);
};

const TimelineLink: React.FunctionComponent = () => {
	const { formatMessage } = useIntl();
	return (
		<PatientLink to="timeline" title={formatMessage(messages.timeline)}>
			<MdTimeline />
		</PatientLink>
	);
};

const ReportLink: React.FunctionComponent = () => {
	const { formatMessage } = useIntl();
	return (
		<PatientLink to="/report" title={formatMessage(messages.report)}>
			<FaFile />
		</PatientLink>
	);
};

const StatementOfConsentAlert: React.FunctionComponent = () => {
	const event = useLastEventByQuery<boolean>(
		queries.signedStatementOfConsent
	);

	const { formatMessage } = useIntl();

	if (
		event !== undefined &&
		event.value === true &&
		event.attachments !== null &&
		event.attachments.length !== 0
	) {
		return null;
	}

	return (
		<PatientLink
			to={`/form/${patientStatementOfConsent.id}`}
			title={formatMessage(messages.statementOfConsentAlert)}
		>
			<HeaderAlert />
		</PatientLink>
	);
};

const Name: React.FunctionComponent = () => {
	const value = usePatientName();
	return <>{value}</>;
};

const SocialSecurityNumber: React.FunctionComponent = () => {
	const value = useSocialSecurityNumber();
	return <HeaderValue variant="social-security-number">{value}</HeaderValue>;
};

const BloodGroup: React.FunctionComponent = () => {
	const users = useKnownUsers();

	const { value, timestamp, origin } = useBloodGroup();

	const { formatDate } = useIntl();
	const formatName = useFormatName();

	const title = renderTitle({
		users,
		timestamp,
		origin,
		formatDate,
		formatName
	});

	let content = <>{value}</>;

	if (origin !== undefined) {
		content = (
			<PatientLink to={`/form/${origin.kind}/${origin.id}`}>
				{content}
			</PatientLink>
		);
	}

	return (
		<Tooltip
			title={title}
			content={<HeaderValue variant="blood-group">{content}</HeaderValue>}
		/>
	);
};

const Age: React.FunctionComponent = () => {
	const value = useAge();
	return <HeaderValue variant="age">{value}</HeaderValue>;
};

const Weight: React.FunctionComponent = () => {
	const users = useKnownUsers();

	const { value, timestamp, origin } = useWeight();

	const { formatDate } = useIntl();
	const formatName = useFormatName();

	const title = renderTitle({
		users,
		timestamp,
		origin,
		formatDate,
		formatName
	});

	let content = <>{value}</>;

	if (origin !== undefined) {
		content = (
			<PatientLink to={`/form/${origin.kind}/${origin.id}`}>
				{content}
			</PatientLink>
		);
	}

	return (
		<Tooltip
			title={title}
			content={<HeaderValue variant="weight">{content}</HeaderValue>}
		/>
	);
};

const Height: React.FunctionComponent = () => {
	const users = useKnownUsers();

	const { value, timestamp, origin } = useHeight();

	const { formatDate } = useIntl();
	const formatName = useFormatName();

	const title = renderTitle({
		users,
		timestamp,
		origin,
		formatDate,
		formatName
	});

	let content = <>{value}</>;

	if (origin !== undefined) {
		content = (
			<PatientLink to={`/form/${origin.kind}/${origin.id}`}>
				{content}
			</PatientLink>
		);
	}

	return (
		<Tooltip
			title={title}
			content={<HeaderValue variant="height">{content}</HeaderValue>}
		/>
	);
};

const Bmi: React.FunctionComponent = () => {
	const users = useKnownUsers();

	const { value, timestamp, origin } = useBmi();

	const { formatDate } = useIntl();
	const formatName = useFormatName();

	const title = renderTitle({
		users,
		timestamp,
		origin,
		formatDate,
		formatName
	});

	let content = (
		<>
			<FormattedMessage {...messages.bmi} />
			{": "}
			{value}
		</>
	);

	if (origin !== undefined) {
		content = (
			<PatientLink to={`/form/${origin.kind}/${origin.id}`}>
				{content}
			</PatientLink>
		);
	}

	return (
		<Tooltip
			title={title}
			content={<HeaderValue variant="bmi">{content}</HeaderValue>}
		/>
	);
};
